// ---------------------------------------------------------------------------
// Exports the project as the same 6 structural CSV files the original
// desktop app writes for its calculation engine to consume (EscribeModelo(),
// see EvalHid_Git/EvalHid/EvalHid/Form1.vb:1799-2115), PLUS whichever of the
// 4 daily input series (precipitation/ETP/temperature/pumping) the project
// actually has loaded (buildSeriesCsv, DEFAULT_SERIES_PATHS naming) — every
// file EvalHid_cal.exe itself would need to run this project, bundled into a
// single .zip (zip.ts), for the user to do whatever they want with (inspect
// in Excel, feed to the real EvalHid_cal.exe, archive...).
//
// The 4 series files were deliberately NOT written here until Néstor asked
// for it directly (this repo's own CLAUDE.md used to document the opposite
// as a firm decision) — the export previously covered only the 6 structural
// files, on the reasoning that the user already has the series data
// wherever they got it from (upload/paste/AEMET). Reversed because a .zip
// missing these 4 files isn't actually usable by the real engine at all
// (Ent_Config.csv's own Fecha_inicio/Fecha_fin point at data files that
// don't exist in the export) — writing exactly what's currently loaded is
// what makes the whole export self-contained.
//
// Deliberately DIFFERENT from the original on two points, both decided with
// Néstor after reviewing the original's actual behavior:
//
//   1. Never aborts, never drops a row. The original has 9 checks that can
//      abort the WHOLE write (missing dates, a sub-basin with no outlet
//      point, an aquifer with bad Vo/Alfa...) and, independently, silently
//      excludes rows that don't pass certain criteria (sub-basins with no
//      runoff model never appear in Ent_Cuencas.csv; aquifers with no valid
//      outlet point are dropped via an INNER JOIN with no warning at all).
//      None of that makes sense for an export whose whole point is "give the
//      user everything so they can do what they want with it" — every
//      sub-basin/zone/outlet point/aquifer the project has gets a row,
//      always, using a fallback (0/blank/the parameter's own default) for
//      anything genuinely missing instead of omitting the row.
//   2. A companion informe.txt lists every gap that would have blocked or
//      silently filtered something in the original — reusing the same
//      detection logic already in validations.ts (the web port's own
//      adaptation of the original's checks) — as pure information, so
//      nothing is lost, it's just surfaced instead of hidden.
//
// Column layouts, separators (";"), the per-model "Ent_{modelo}_Param.csv"
// naming, and the static Etiquetas lookup table were confirmed by reading
// Form1.vb directly (WriteArchivoCuencas/Acuiferos/Aportaciones/
// Parametros/Config/Etiquetas, lines 1810-2115) — default file names for the
// other 5 were confirmed by reading APO_ARCHIVOS_SIMULACION from a real
// project (test/fixtures/ModeloTuria/EvalhidTuria2026.ehd) with mdb-reader.
// Always the DEFAULT names (DEFAULT_PATHS), deliberately NOT the project's
// own possibly-customized APO_ARCHIVOS_SIMULACION.RUTA (unlike the original,
// which reads a per-project path there) — Néstor's call: one fixed, known
// set of names is simpler to work with than a name that can silently vary
// per project.
//
// A third deliberate deviation from the original, on top of the two below:
// zones are cross-referenced by NAME (Zona_parametros/Zona_parametros_nieve
// in Ent_Cuencas.csv, and the first column of each Ent_{modelo}_Param.csv),
// not by their internal numeric code — the original writes the raw
// COD_ZONA/COD_ZONA_CUENCA there instead, which throws the zone's real name
// away entirely (nothing in these 6 files ever carries it, since APO_ZONAS
// itself isn't one of them). Same convention already used for
// Cod_acuifero/Cod_aportacion (also names, not codes) — a zone's own numeric
// code has no meaning outside this one project anyway, so it isn't worth
// preserving over the name.
// ---------------------------------------------------------------------------
import { validateProject } from "./validations.ts";
import { aquiferOption } from "./calculationOptions.ts";
import { activeUnitsCoefficient } from "./units.ts";
import { isoToDdmmyyyy } from "./isoDate.ts";
import { sanitizedProjectFileName } from "./fileNaming.ts";
import { buildZip } from "./zip.ts";
import {
  DEFAULT_PATHS,
  DEFAULT_SERIES_PATHS,
  LABEL_AQUIFERS,
  LABEL_OUTLET_POINTS,
  LABEL_CONFIG,
  LABEL_SUBBASINS,
  LABEL_LABELS,
  paramFileName,
} from "./csvProjectFileNames.ts";
import { sortedByCode } from "./csvProjectParsing.ts";
import type { DailySeries } from "./dailySeries.ts";
import type { LoadedProject } from "./project.ts";
import type { EvalHidProject } from "./types.ts";
import type { Translate } from "../i18n/translate.ts";

function csvLine(fields: (string | number)[]): string {
  return fields.join(";");
}

interface CsvExportFile {
  name: string;
  content: string;
}

export function buildSubBasinsCsv(project: EvalHidProject): CsvExportFile {
  const header = csvLine([
    "Codigo",
    "Superf",
    "Cod_modelo",
    "Cod_acuifero",
    "Cod_aportacion",
    "Coef_precipitacion",
    "Coef_etp",
    "Coef_temperatura",
    "Res_parciales",
    "Zona_parametros",
    "Cod_modelo_nieve",
    "Zona_parametros_nieve",
  ]);
  const rows = project.subBasins.map((c) => {
    const aquifer = project.aquifers.find((a) => a.aquiferCode === c.aquiferCode);
    const outletPoint = project.outletPoints.find((p) => p.outletCode === c.outletCode);
    const zone = c.zoneCode != null ? project.zones.find((z) => z.zoneCode === c.zoneCode) : null;
    const snowZone =
      c.snowZoneCode != null ? project.zones.find((z) => z.zoneCode === c.snowZoneCode) : null;
    // Matched on (modelTypeCode, kind) together, not modelTypeCode alone —
    // codes are only unique WITHIN a kind (code 1 is "Témez" for kind=0 but
    // "Nieve1" for kind=1, see baseTemplate.ts's MODEL_TYPES).
    const model = project.modelTypes.find(
      (m) => m.modelTypeCode === c.modelTypeCode && m.kind === 0
    );
    const snowModel = project.modelTypes.find(
      (m) => m.modelTypeCode === c.snowModelTypeCode && m.kind === 1
    );
    return csvLine([
      c.description,
      c.area,
      model?.modelTypeName ?? "",
      c.aquiferCode === 0 ? "0" : (aquifer?.name ?? "0"),
      c.outletCode == null ? "0" : (outletPoint?.name ?? "0"),
      c.precipAdjustment,
      c.etpAdjustment,
      c.tempAdjustment,
      c.partialResults,
      c.zoneCode == null ? "0" : (zone?.name ?? "0"),
      snowModel?.modelTypeName ?? "0",
      c.snowZoneCode == null ? "0" : (snowZone?.name ?? "0"),
    ]);
  });
  return { name: DEFAULT_PATHS[LABEL_SUBBASINS], content: [header, ...rows].join("\r\n") };
}

export function buildAquifersCsv(project: EvalHidProject): CsvExportFile {
  const header = csvLine(["Codigo", "Nombre", "Vo", "Alfa", "Cod_Apo"]);
  const rows = project.aquifers.map((a) => {
    const outletPoint = project.outletPoints.find((p) => p.outletCode === a.outletCode);
    return csvLine([a.name, a.name, a.vo ?? "", a.alfa ?? "", outletPoint?.name ?? ""]);
  });
  return { name: DEFAULT_PATHS[LABEL_AQUIFERS], content: [header, ...rows].join("\r\n") };
}

export function buildOutletPointsCsv(project: EvalHidProject): CsvExportFile {
  const header = csvLine(["Codigo", "Nombre"]);
  const rows = project.outletPoints.map((p) => csvLine([p.name, p.name]));
  return { name: DEFAULT_PATHS[LABEL_OUTLET_POINTS], content: [header, ...rows].join("\r\n") };
}

/** One `Ent_{modelo}_Param.csv` per row of the model catalog (project.modelTypes) — always written, even for a model no zone currently uses (header-only), same as the original (its own skip-if-empty check exists in source but is commented out, see Form1.vb:1935). */
export function buildParametrosCsvFiles(project: EvalHidProject): CsvExportFile[] {
  return project.modelTypes.map((model) => {
    const paramDefs = sortedByCode(
      project.parameterDefinitions.filter(
        (d) => d.modelTypeCode === model.modelTypeCode && d.kind === model.kind
      ),
      (d) => d.parameterCode
    );
    const stateDefs = sortedByCode(
      project.stateVariableDefinitions.filter(
        (d) => d.modelTypeCode === model.modelTypeCode && d.kind === model.kind
      ),
      (d) => d.stateVariableCode
    );

    const zoneCodeHeader = model.modelTypeName.includes("Nieve") ? "Cod_Zona_Nieve" : "Cod_Zona";
    const header = csvLine([
      zoneCodeHeader,
      ...paramDefs.map((d) => d.name),
      ...stateDefs.map((d) => d.name),
    ]);

    const zonesForModel = project.zones.filter((z) =>
      model.kind === 0
        ? z.modelTypeCode === model.modelTypeCode
        : z.snowModelTypeCode === model.modelTypeCode
    );
    const rows = zonesForModel.map((zone) => {
      const paramValues = paramDefs.map((d) => {
        const found = project.parameterValues.find(
          (v) =>
            v.zoneCode === zone.zoneCode && v.parameterCode === d.parameterCode && v.kind === d.kind
        );
        return found?.value ?? d.defaultValue ?? 0;
      });
      const stateValues = stateDefs.map((d) => {
        const found = project.stateVariableValues.find(
          (v) =>
            v.zoneCode === zone.zoneCode &&
            v.stateVariableCode === d.stateVariableCode &&
            v.kind === d.kind
        );
        return found?.value ?? d.defaultValue ?? 0;
      });
      return csvLine([zone.name, ...paramValues, ...stateValues]);
    });

    return { name: paramFileName(model.modelTypeName), content: [header, ...rows].join("\r\n") };
  });
}

export function buildConfigCsv(
  project: EvalHidProject,
  series: LoadedProject["series"]
): CsvExportFile {
  const header = csvLine(["Concepto", "Valor", "Observaciones"]);
  const startDate = series.precipitation?.dates[0];
  const endDate = series.precipitation?.dates[series.precipitation.dates.length - 1];
  const rows = [
    csvLine([
      "Unidades",
      activeUnitsCoefficient(project),
      "Para el paso de mm a hm3 (si la superficie es Km2 entonces 1/1000)",
    ]),
    csvLine([
      "Fecha_inicio",
      startDate ? isoToDdmmyyyy(startDate) : "",
      "Fecha de inicio de simulación",
    ]),
    csvLine(["Fecha_fin", endDate ? isoToDdmmyyyy(endDate) : "", "Fecha de fin de simulación"]),
    csvLine(["Escala_Temporal", project.seriesScale === "monthly" ? 2 : 1, "1 día, 2 mes"]),
    csvLine(["Acumulacion", 0, "0 Sin acumulación, 1 Acumula mensual (sim diaria)"]),
    csvLine(["Simbolo_res_par", "_", "Símbolo para los resultados parciales (cuenca resultado)"]),
    csvLine([
      "Op_neg_acu",
      aquiferOption(project, 1) ? 1 : 0,
      "0 no permite negativos en la modelación de acuífero, 1 sí que permite negativos en volumen y aportación",
    ]),
    csvLine([
      "Op_bombeos",
      aquiferOption(project, 2) ? 1 : 0,
      "0 bombeos desactivados, 1 bombeos activados",
    ]),
    csvLine([
      "Op_apo_cuenc",
      0,
      "0 no escribe Sal_apo_subcuencas.csv, 1 sí lo escribe (aportación por subcuenca)",
    ]),
  ];
  return { name: DEFAULT_PATHS[LABEL_CONFIG], content: [header, ...rows].join("\r\n") };
}

/** Fully static (error-code lookup, Form1.vb:2010-2032) — no per-project data at all, quoted verbatim from the original source. */
export function buildLabelsCsv(): CsvExportFile {
  const header = csvLine(["Cod", "Etiqueta"]);
  const rows = [
    [1000, "Cabecera o etiqueta no encontrada"],
    [1001, "No se ha encontrado el archivo"],
    [1002, "Error en la lectura de archivo"],
    [1003, "Falta información en el archivo de coeficientes"],
    [1004, "Falta una cabecera en el archivo"],
    [1005, "Archivo de datos bloqueado"],
    [1006, "No se ha encontrado fecha de inicio"],
    [1007, "Fecha final de archivo anterior al período de simulación"],
    [1008, "Dato erróneo"],
    [1009, "Código de elemento repetido"],
    [1010, "Código de tipo de modelo repetido"],
    [1011, "Archivo de resultados bloqueado"],
    [1012, "Falta un código de cuenca en el archivo"],
    [1013, "Código de zona o parámetro en el archivo de parámetros repetido"],
    [1014, "Falta parámetro en el archivo de parámetros"],
    [1015, "Código de acuífero repetido en masa de agua"],
    [
      4001,
      "La siguiente cuenca tiene un código de aportación que no existe en el archivo de aportaciones",
    ],
    [4002, "Problemas al cerrar archivo de escritura"],
    [
      4003,
      "La siguiente cuenca tiene un código de acuífero que no existe en el archivo de acuíferos",
    ],
    [
      4004,
      "El siguiente acuífero tiene un código de aportación que no existe en el archivo de aportaciones",
    ],
    [5001, "Parámetro fuera de rango. CodZona/Parámetro"],
    [
      5002,
      "Las fechas están en escala diaria y la escala de la simulación se ha definido en mensual",
    ],
    [
      5003,
      "Las fechas están en escala mensual y la escala de la simulación se ha definido como diaria",
    ],
    [3001, "Modelo o modelo de nieve no reconocido"],
    [3002, "El código de un acuífero no puede ser 0"],
  ] as const;
  return {
    name: DEFAULT_PATHS[LABEL_LABELS],
    content: [header, ...rows.map(([cod, label]) => csvLine([cod, label]))].join("\r\n"),
  };
}

/** Names repeated within the same catalog — the export uses each entity's NAME as its cross-file lookup key (Cod_acuifero/Cod_aportacion in Ent_Cuencas.csv), same as the original, so two entities sharing a name make that lookup ambiguous. */
function duplicateNames(names: string[]): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const name of names) {
    if (seen.has(name)) dupes.add(name);
    seen.add(name);
  }
  return [...dupes];
}

/**
 * Everything the original would have blocked or silently dropped, listed as
 * pure information instead — reuses validateProject (already the web port's
 * own translation of the original's checks, see validations.ts) for the
 * bulk of it, plus two checks validateProject doesn't cover because they
 * don't affect calculation, only the exported files themselves:
 *   - an aquifer with no outlet point at all (validateProject only checks
 *     ones actually referenced by a sub-basin — this export writes every
 *     aquifer, referenced or not, so the check needs to too)
 *   - duplicate names within a catalog (see duplicateNames above)
 */
export function buildReportFindings(
  t: Translate,
  project: EvalHidProject,
  series: LoadedProject["series"]
): string[] {
  const validation = validateProject(t, project, series);
  const findings = [...validation.blocking, ...validation.notices];

  const aquifersWithoutOutlet = project.aquifers.filter((a) => a.outletCode == null);
  if (aquifersWithoutOutlet.length > 0) {
    findings.push(
      t("exportReport.aquifersWithoutOutlet", {
        n: aquifersWithoutOutlet.length,
        list: aquifersWithoutOutlet.map((a) => a.name).join(", "),
      })
    );
  }

  const dupSubBasins = duplicateNames(project.subBasins.map((c) => c.description));
  if (dupSubBasins.length > 0) {
    findings.push(
      t("exportReport.duplicateNames", {
        kind: t("panels.subBasins"),
        list: dupSubBasins.join(", "),
      })
    );
  }
  const dupAquifers = duplicateNames(project.aquifers.map((a) => a.name));
  if (dupAquifers.length > 0) {
    findings.push(
      t("exportReport.duplicateNames", {
        kind: t("infoPopover.aquifers"),
        list: dupAquifers.join(", "),
      })
    );
  }
  const dupOutlets = duplicateNames(project.outletPoints.map((p) => p.name));
  if (dupOutlets.length > 0) {
    findings.push(
      t("exportReport.duplicateNames", {
        kind: t("infoPopover.outletPoints"),
        list: dupOutlets.join(", "),
      })
    );
  }
  // Zones are cross-referenced by NAME now (see this file's own header) —
  // checked separately per kind, since a runoff zone and a snow zone CAN
  // share a name without any ambiguity (different columns/files reference
  // each), but two zones of the SAME kind can't.
  const dupRunoffZones = duplicateNames(
    project.zones.filter((z) => z.modelTypeCode != null).map((z) => z.name)
  );
  if (dupRunoffZones.length > 0) {
    findings.push(
      t("exportReport.duplicateNames", {
        kind: t("panels.zones"),
        list: dupRunoffZones.join(", "),
      })
    );
  }
  const dupSnowZones = duplicateNames(
    project.zones.filter((z) => z.snowModelTypeCode != null).map((z) => z.name)
  );
  if (dupSnowZones.length > 0) {
    findings.push(
      t("exportReport.duplicateNames", {
        kind: t("canvas.snowZonesTab"),
        list: dupSnowZones.join(", "),
      })
    );
  }

  return findings;
}

function buildReportTxt(
  t: Translate,
  project: EvalHidProject,
  series: LoadedProject["series"]
): CsvExportFile {
  const findings = buildReportFindings(t, project, series);
  const lines = [
    t("exportReport.title"),
    "",
    findings.length > 0
      ? t("exportReport.introWithFindings", { n: findings.length })
      : t("exportReport.introNoFindings"),
    "",
    ...findings.map((f) => `- ${f}`),
  ];
  return { name: "informe.txt", content: lines.join("\r\n") };
}

/**
 * The v2 daily-series format `dailySeries.ts`'s own `parseDailySeriesCsv`
 * reads back (`Fecha;<col1>;<col2>;...`, `;`-separated, dd/mm/yyyy dates,
 * an empty cell for a `NaN` value — the exact inverse of that parser, so a
 * round-trip through this exporter and back changes nothing). Named for
 * whichever of `DEFAULT_SERIES_PATHS` the caller passes: `Ent_Precip.csv`/
 * `Ent_Etp.csv`/`Ent_Tempe.csv` columns are sub-basin names
 * (`SubBasin.description`); `Ent_Bomb.csv`'s columns mix sub-basin AND
 * aquifer names in the very same file (`SeriesTemporales.cs`'s own
 * `loc_arch_bomb`, CLAUDE.md's "Bloque 3") — this function doesn't need to
 * know which, since `series.columns` already carries whatever names were
 * there when the series was loaded/pasted.
 */
export function buildSeriesCsv(name: string, series: DailySeries): CsvExportFile {
  const header = csvLine(["Fecha", ...series.columns]);
  const rows = series.dates.map((iso, row) =>
    csvLine([
      isoToDdmmyyyy(iso),
      ...series.values.map((col) => (Number.isFinite(col[row]) ? col[row] : "")),
    ])
  );
  return { name, content: [header, ...rows].join("\r\n") };
}

/** Builds the complete CSV export (6 real files + the 4 daily input series, whichever are actually loaded + one per model catalog entry + the findings report) as a single downloadable .zip. */
export async function buildProjectCsvZip(
  t: Translate,
  project: EvalHidProject,
  series: LoadedProject["series"]
): Promise<Blob> {
  const files: CsvExportFile[] = [
    buildSubBasinsCsv(project),
    buildAquifersCsv(project),
    buildOutletPointsCsv(project),
    ...buildParametrosCsvFiles(project),
    buildConfigCsv(project, series),
    buildLabelsCsv(),
    buildReportTxt(t, project, series),
  ];
  if (series.precipitation)
    files.push(buildSeriesCsv(DEFAULT_SERIES_PATHS.precipitation, series.precipitation));
  if (series.etp) files.push(buildSeriesCsv(DEFAULT_SERIES_PATHS.etp, series.etp));
  if (series.temperature)
    files.push(buildSeriesCsv(DEFAULT_SERIES_PATHS.temperature, series.temperature));
  if (series.pumping) files.push(buildSeriesCsv(DEFAULT_SERIES_PATHS.pumping, series.pumping));
  const encoder = new TextEncoder();
  return buildZip(files.map((f) => ({ name: f.name, content: encoder.encode(f.content) })));
}

/** Suggested download name — a .zip, distinct from exportProject.ts's own .ehdz (a different format, portable backup vs. the desktop app's own CSV scheme). */
export function csvExportFileName(projectName: string): string {
  return `${sanitizedProjectFileName(projectName)}-csv.zip`;
}
